import React, { useState, useRef, useEffect } from "react";
import CommonTable from "../../components/commonComponent/CommanTable";
import CommanFilters from "../../components/commonComponent/CommanFilters";
import {
  FaEye,
  FaEllipsisV,
  FaEdit,
  FaTrashAlt,
  FaFilter,
  FaPlus,
} from "react-icons/fa";
import { Link, useNavigate } from "react-router-dom";
import axios from "../../utils/axiosInstance";
import { confirmDelete } from "../../utils/alertHelper";
import { hasPermission } from "../../utils/permissionHelper";

/* ================= COLUMNS ================= */
const allFields = [
  { key: "srNo", label: "Sr.No." },
  { key: "code", label: "Item Code" },
  { key: "itemName", label: "Item Name" },
  { key: "skucode", label: "SKU Code" },
  { key: "group", label: "Group" },
  { key: "unit", label: "Unit" },
  { key: "status", label: "Status" },
  { key: "salesPrice", label: "Price (₹)" },
  { key: "description", label: "Description" },
];

function ItemIndex() {
  const navigate = useNavigate();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState({
    group: "",
    category: "",
    status: "",
  });
  const [filterDropdowns, setFilterDropdowns] = useState({ groups: [], categories: [], statuses: [
    { value: 'Available', label: 'Available' },
    { value: 'Not Available', label: 'Not Available' }
  ] });

  const getOptionVal = (it) => (it && (it._id ?? it.id ?? it.value)) || '';
  const getOptionLabel = (it) => it?.name || it?.title || it?.label || '';
  const buildOptions = (list) => (Array.isArray(list) ? list.map(it => ({ value: getOptionVal(it), label: getOptionLabel(it) })) : []);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const [groupRes, catRes] = await Promise.all([
          axios.get(`/admin/api/item-group`),
          axios.get(`/admin/api/itemCategory`),
        ]);

        const groups = (groupRes?.data && (groupRes.data.groups || groupRes.data.data || groupRes.data)) || [];
        const categories = (catRes?.data && (catRes.data.categories || catRes.data.data || catRes.data)) || [];
        if (!mounted) return;
        setFilterDropdowns(prev => ({ ...prev, groups: buildOptions(groups), categories: buildOptions(categories) }));
      } catch (e) {
        // ignore
      }
    })();
    return () => { mounted = false; };
  }, []);

  const clearFiltersDefault = { group: '', category: '', status: '' };
  const handleClearFilters = () => {
    setFilters(clearFiltersDefault);
    // pass cleared filters to fetchItems so the request uses the cleared values immediately
    fetchItems(clearFiltersDefault);
  };

  const statusColors = {
    Available: { bg: "#DCF4E9", fg: "#12B76A" },
    "Not Available": { bg: "#FADFDF", fg: "#DC2626" },
  };

  const renderBadge = (type, value) => {
    if (!value) return "-";
    const map = type === 'status' ? statusColors[value] || {} : {};
    const bg = map.bg || '#F0F0F0';
    const fg = map.fg || '#333';
    return (
      <div className="d-flex align-items-center gap-2">
        <span
          style={{
            display: 'inline-block',
            padding: '6px 10px',
            borderRadius: 999,
            background: bg,
            color: fg,
            fontWeight: 400,
            fontSize: '0.85rem',
          }}
        >
          {value}
        </span>
      </div>
    );
  };
  const [showFilters, setShowFilters] = useState(false);

  /* ACTION MENU */
  const [actionMenuOpen, setActionMenuOpen] = useState(null);
  const actionMenuRef = useRef(null);

  /* ================= USER ================= */
  const [user, setUser] = useState(null);
  useEffect(() => {
    const stored = localStorage.getItem("user");
    if (stored) setUser(JSON.parse(stored));
  }, []);

  const isClientUser = user?.userType === "Client";
  const userStateId = user?.state?._id || user?.state || null;

  /* ================= PERMISSIONS ================= */
  const canListItem = hasPermission(user, "Item Master", "List");
  const canViewItem = hasPermission(user, "Item Master", "View");
  const canCreateItem = hasPermission(user, "Item Master", "Create");
  const canEditItem = hasPermission(user, "Item Master", "Edit");
  const canDeleteItem = hasPermission(user, "Item Master", "Delete");

  /* ================= CLOSE DROPDOWN (FIX) ================= */
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        actionMenuRef.current &&
        !actionMenuRef.current.contains(e.target)
      ) {
        setActionMenuOpen(null);
      }
    };

    const handleScroll = () => setActionMenuOpen(null);

    document.addEventListener("mousedown", handleClickOutside);
    window.addEventListener("scroll", handleScroll, true);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, []);

  /* ================= FETCH ITEMS ================= */
  const fetchItems = async (overrideFilters) => {
    if (!canListItem && !canViewItem && !isClientUser) return;

    try {
      setLoading(true);
      const qp = new URLSearchParams({ page: 1, limit: 1000 });
      if (isClientUser && userStateId) qp.append("state", userStateId);

      const current = overrideFilters || filters || {};
      if (current.group) qp.append('group', current.group);
      if (current.category) qp.append('category', current.category);
      if (current.status) qp.append('status', current.status);

      // include search when length >= 3 to avoid noise (available for all users)
      if (searchTerm && String(searchTerm).trim().length >= 3) {
        qp.append('search', String(searchTerm).trim());
      }

      const res = await axios.get(`/admin/api/item?${qp}`);
      setItems(res.data?.Items || res.data?.data || res.data || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
  }, [user]);

  // Debounce search input and trigger fetch
  useEffect(() => {
    const id = setTimeout(() => {
      // fetch when search text changed (or cleared)
      fetchItems();
    }, 350);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  /* ================= DELETE ================= */
  const handleDelete = async (item) => {
    if (!canDeleteItem) return;
    const res = await confirmDelete(`Delete ${item.name}?`);
    if (!res.isConfirmed) return;
    await axios.delete(`/admin/api/item/${item._id}`);
    fetchItems();
  };

  /* ================= TABLE DATA ================= */
  const tableData = items.map((item, idx) => ({
    srNo: idx + 1,
    code: item.code,
    itemName: item.name,
    skucode: item.skucode || "-",
    group: item.group?.name || "-",
    unit: item.unit?.name || "-",
    status: renderBadge('status', item.isAvailable ? "Available" : "Not Available"),
    salesPrice: item.salesPrice,
    description: item.description || "-",

    actions: isClientUser ? null : (
      <div className="d-flex align-items-center" style={{ position: "relative" }}>
        {(canEditItem || canDeleteItem) ? (
          <>
            <button
              className="btn btn-sm shadow-none border-0 bg-transparent"
              onClick={(e) => {
                e.stopPropagation();
                setActionMenuOpen(
                  actionMenuOpen === item._id ? null : item._id
                );
              }}
            >
              <FaEllipsisV size={14} />
            </button>

            {actionMenuOpen === item._id && (
              <div
                ref={actionMenuRef}
                onClick={(e) => e.stopPropagation()}
                style={{
                  position: "fixed",
                  zIndex: 1050,
                  background: "#fff",
                  border: "1px solid #ddd",
                  borderRadius: 6,
                  boxShadow: "0 2px 10px rgba(0,0,0,0.15)",
                  minWidth: 150,
                  padding: "6px 0",
                }}
              >
                {canViewItem && (
                  <button
                    className="btn btn-sm w-100 text-start border-0"
                    onClick={() => {
                      setActionMenuOpen(null);
                      navigate(`/admin/item/view/${item._id}`);
                    }}
                  >
                    <FaEye /> View
                  </button>
                )}

                {canEditItem && (
                  <button
                    className="btn btn-sm w-100 text-start border-0"
                    onClick={() => {
                      setActionMenuOpen(null);
                      navigate(`/admin/item/edit/${item._id}`);
                    }}
                  >
                    <FaEdit /> Edit
                  </button>
                )}

                {canDeleteItem && (
                  <button
                    className="btn btn-sm w-100 text-start border-0 text-danger"
                    onClick={() => {
                      setActionMenuOpen(null);
                      handleDelete(item);
                    }}
                  >
                    <FaTrashAlt /> Delete
                  </button>
                )}
              </div>
            )}
          </>
        ) : (
          canViewItem && (
            <Link to={`/admin/item/view/${item._id}`}>
              <FaEye />
            </Link>
          )
        )}
      </div>
    ),
  }));

  const tableColumns = isClientUser
    ? allFields
    : [...allFields, { key: "actions", label: "Action" }];

  /* ================= UI ================= */
  return (
    <div className="container-fluid mt-4">
      <div className="d-flex justify-content-between mb-3">
        <h2>Items</h2>

        <div>
          {!isClientUser && (
            <div className="d-flex gap-2 mb-2 justify-content-end">
              <button
                className="btn btn-light btn-sm"
                onClick={() => setShowFilters(!showFilters)}
              >
                <FaFilter /> Filter
              </button>

              {canCreateItem && (
                <button
                  className="btn btn-primary btn-sm"
                  onClick={() => navigate("/admin/item/create")}
                >
                  <FaPlus /> Add New Item
                </button>
              )}
            </div>
          )}

          <input
            className="form-control"
            style={{ width: 250 }}
            placeholder="Search item..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        </div>

      {!isClientUser && showFilters && (
        <CommanFilters
          filters={filters}
          dropdowns={filterDropdowns}
          visibleFields={['group', 'category', 'status']}
          onFilterChange={(k, v) =>
            setFilters((f) => ({ ...f, [k]: v }))
          }
          onApplyFilters={fetchItems}
          onClearFilters={handleClearFilters}
        />
      )}

      <CommonTable columns={tableColumns} data={tableData} loading={loading} />
    </div>
  );
}

export default ItemIndex;
